import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaExternalLinkAlt, FaChevronLeft, FaChevronRight, FaExpand, FaArrowLeft } from 'react-icons/fa';
import { ImageWithFallback } from './ImageWithFallback';

const GalleryPreview = ({ galeria }) => (
  <div className="relative w-full max-h-[70vh] overflow-hidden bg-dark-bg dark:bg-black">
    {galeria.length === 1 ? (
      <ImageWithFallback
        src={galeria[0].src}
        alt={galeria[0].alt}
        className="w-full max-h-[70vh] object-contain mx-auto"
        placeholderLabel={galeria[0].alt}
      />
    ) : (
      <motion.div
        className="flex flex-col"
        animate={{ y: [0, -(galeria.length - 1) * 300] }}
        transition={{
          duration: galeria.length * 5,
          repeat: Infinity,
          repeatType: 'reverse',
          ease: 'easeInOut',
        }}
      >
        {galeria.map((img, idx) => (
          <ImageWithFallback
            key={idx}
            src={img.src}
            alt={img.alt}
            className="w-full max-h-[70vh] object-contain flex-shrink-0 mx-auto"
            placeholderLabel={img.alt}
          />
        ))}
      </motion.div>
    )}
    <div className="absolute inset-0 bg-gradient-to-t from-dark-bg/60 to-transparent pointer-events-none" />
  </div>
);

const GalleryLightbox = ({ images, activeIndex, onClose, onNavigate }) => {
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onNavigate(-1);
      if (e.key === 'ArrowRight') onNavigate(1);
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose, onNavigate]);

  const current = images[activeIndex];
  const hasPrev = activeIndex > 0;
  const hasNext = activeIndex < images.length - 1;

  return (
    <motion.div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/95 backdrop-blur-sm p-4 md:p-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-burnt text-white flex items-center justify-center transition-colors"
        aria-label="Fechar galeria"
        data-cursor="pointer"
      >
        <FaTimes className="text-xl" />
      </button>

      {hasPrev && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(-1);
          }}
          className="absolute left-2 md:left-6 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-burnt text-white flex items-center justify-center transition-colors"
          aria-label="Imagem anterior"
          data-cursor="pointer"
        >
          <FaChevronLeft className="text-xl" />
        </button>
      )}

      {hasNext && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate(1);
          }}
          className="absolute right-2 md:right-6 z-10 w-12 h-12 rounded-full bg-white/10 hover:bg-burnt text-white flex items-center justify-center transition-colors"
          aria-label="Próxima imagem"
          data-cursor="pointer"
        >
          <FaChevronRight className="text-xl" />
        </button>
      )}

      <motion.div
        key={activeIndex}
        className="relative max-w-[95vw] max-h-[90vh] flex flex-col items-center"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.25 }}
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={current.src}
          alt={current.alt}
          className="max-w-full max-h-[85vh] w-auto h-auto object-contain rounded-lg"
        />
        <p className="mt-4 text-cream/80 text-sm font-body text-center">{current.alt}</p>
        <p className="mt-1 text-cream/50 text-xs font-body">
          {activeIndex + 1} / {images.length}
        </p>
      </motion.div>
    </motion.div>
  );
};

const ProjectModal = ({ project, onClose }) => {
  const videoRef = useRef(null);
  const lightboxIndexRef = useRef(null);
  const [videoError, setVideoError] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const showVideo = project?.video && !videoError;

  lightboxIndexRef.current = lightboxIndex;

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  const navigateLightbox = useCallback(
    (direction) => {
      setLightboxIndex((prev) => {
        if (prev === null) return prev;
        const next = prev + direction;
        if (next < 0 || next >= project.galeria.length) return prev;
        return next;
      });
    },
    [project?.galeria?.length]
  );

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    setVideoError(false);
    setLightboxIndex(null);

    if (videoRef.current && project?.video) {
      videoRef.current.play().catch(() => setVideoError(true));
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [project]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key !== 'Escape') return;
      if (lightboxIndexRef.current !== null) {
        closeLightbox();
      } else {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose, closeLightbox]);

  if (!project) return null;

  const handleVisitSite = () => {
    if (project.link) {
      window.open(project.link, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[9998] flex justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <motion.div
          className="fixed inset-0 bg-dark-bg/90 dark:bg-black/95 backdrop-blur-md"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          aria-hidden="true"
        />

        <motion.div
          className="relative w-full h-[100dvh] flex flex-col bg-cream dark:bg-dark-bg overflow-hidden"
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          data-lenis-prevent
          onClick={(e) => e.stopPropagation()}
        >
          <header className="sticky top-0 z-50 flex-shrink-0 flex items-center justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4 bg-cream/95 dark:bg-dark-bg/95 backdrop-blur-md border-b border-brown/10 dark:border-burnt/10 shadow-sm">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-dark dark:text-cream hover:bg-dark/5 dark:hover:bg-white/10 transition-colors"
              aria-label="Voltar aos projetos"
              data-cursor="pointer"
            >
              <FaArrowLeft className="text-burnt" />
              <span>Voltar</span>
            </button>

            <p className="hidden sm:block flex-1 min-w-0 text-center font-heading font-semibold text-dark dark:text-cream text-sm md:text-base truncate px-2">
              {project.titulo}
            </p>

            <button
              type="button"
              onClick={onClose}
              className="w-11 h-11 rounded-full bg-dark/10 dark:bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-burnt hover:text-white transition-all duration-300 text-dark dark:text-cream flex-shrink-0"
              aria-label="Fechar"
              data-cursor="pointer"
            >
              <FaTimes className="text-lg" />
            </button>
          </header>

          <div className="flex-1 overflow-y-auto overscroll-contain">
          <div className="w-full bg-dark-bg dark:bg-black">
            {showVideo ? (
              <video
                ref={videoRef}
                src={project.video}
                className="w-full max-h-[70vh] object-cover"
                autoPlay
                muted
                loop
                playsInline
                onError={() => setVideoError(true)}
              />
            ) : (
              <GalleryPreview galeria={project.galeria} />
            )}
          </div>

          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
            <span className="inline-block px-4 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-burnt/10 text-burnt mb-2">
              {project.segmento}
            </span>
            <span className="inline-block ml-2 px-4 py-1 rounded-full text-xs font-semibold uppercase tracking-wider bg-dark/5 dark:bg-white/5 text-dark/70 dark:text-cream/70 mb-4">
              {project.categoria === 'sites' ? 'Site' : 'Sistema'}
            </span>

            <h2 className="text-3xl md:text-5xl font-heading font-bold text-dark dark:text-cream mb-8">
              {project.titulo}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-burnt mb-2">Objetivo</h3>
                <p className="text-dark/80 dark:text-cream/80 leading-relaxed font-body">{project.objetivo}</p>
              </div>
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-burnt mb-2">Solução</h3>
                <p className="text-dark/80 dark:text-cream/80 leading-relaxed font-body">{project.solucao}</p>
              </div>
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-burnt mb-2">Desafios</h3>
                <p className="text-dark/80 dark:text-cream/80 leading-relaxed font-body">{project.desafios}</p>
              </div>
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-burnt mb-2">Como resolvi</h3>
                <p className="text-dark/80 dark:text-cream/80 leading-relaxed font-body">{project.solucaoDesafio}</p>
              </div>
            </div>

            {project.galeria.length > 0 && (
              <div className="mb-12">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-burnt mb-2">Galeria</h3>
                <p className="text-dark/60 dark:text-cream/60 text-sm font-body mb-6">
                  Clique na imagem para ampliar e navegar entre elas
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {project.galeria.map((img, idx) => (
                    <motion.button
                      key={idx}
                      type="button"
                      onClick={() => setLightboxIndex(idx)}
                      className="group relative rounded-2xl overflow-hidden shadow-card dark:shadow-card-dark border border-brown/10 dark:border-burnt/10 bg-dark-bg/5 dark:bg-black/20 text-left w-full"
                      whileHover={{ scale: 1.01, y: -2 }}
                      transition={{ duration: 0.3 }}
                      data-cursor="pointer"
                      aria-label={`Ampliar: ${img.alt}`}
                    >
                      <div className="flex items-center justify-center min-h-[180px] md:min-h-[220px] p-3">
                        <ImageWithFallback
                          src={img.src}
                          alt={img.alt}
                          className="w-full h-auto max-h-[220px] object-contain"
                          placeholderLabel={img.alt}
                        />
                      </div>
                      <div className="absolute inset-0 bg-burnt/0 group-hover:bg-burnt/10 transition-colors duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <span className="flex items-center gap-2 bg-dark/70 text-white text-xs font-semibold px-3 py-2 rounded-full">
                          <FaExpand className="text-sm" />
                          Ampliar
                        </span>
                      </div>
                      <p className="px-4 py-3 text-xs text-dark/70 dark:text-cream/70 font-body border-t border-brown/10 dark:border-burnt/10">
                        {img.alt}
                      </p>
                    </motion.button>
                  ))}
                </div>
              </div>
            )}

            {project.link && (
              <motion.button
                onClick={handleVisitSite}
                className="group inline-flex items-center gap-3 bg-burnt hover:bg-primary-700 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 shadow-premium"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                data-cursor="pointer"
              >
                {project.linkLabel || 'Visitar Site'}
                <FaExternalLinkAlt className="group-hover:translate-x-1 transition-transform" />
              </motion.button>
            )}

            <div className="mt-10 pt-8 border-t border-brown/10 dark:border-burnt/10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-base border-2 border-dark/15 dark:border-cream/20 text-dark dark:text-cream hover:border-burnt hover:text-burnt dark:hover:text-burnt transition-colors"
                data-cursor="pointer"
              >
                <FaArrowLeft className="text-sm" />
                Fechar projeto
              </button>
              <p className="text-dark/45 dark:text-cream/45 text-xs font-body sm:ml-2">
                Ou pressione <kbd className="px-1.5 py-0.5 rounded bg-dark/5 dark:bg-white/10 font-mono text-[10px]">Esc</kbd>
              </p>
            </div>
          </div>
          </div>
        </motion.div>

        <AnimatePresence>
          {lightboxIndex !== null && (
            <GalleryLightbox
              images={project.galeria}
              activeIndex={lightboxIndex}
              onClose={closeLightbox}
              onNavigate={navigateLightbox}
            />
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
};

export default ProjectModal;
